import { useMemo } from "react";
import { addDays } from "@hicya/habits-shared/engine";
import cn from "classnames";

import { HabitIcon } from "@/components/ui/habit-icon";
import { ScoreRing } from "@/components/ui/score-ring";
import type { HabitDisplay } from "@/hooks/use-daily-view";

import styles from "./stats-sidebar.module.scss";

interface StatsSidebarProps {
  allHabits: HabitDisplay[];
  totalDue: number;
  totalDone: number;
  today: string;
  completions: Array<{ habit_id: string; effective_date: string; type: string }>;
  firstDayOfWeek: number;
}

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

export function StatsSidebar({
  allHabits,
  totalDue,
  totalDone,
  today,
  completions,
  firstDayOfWeek,
}: StatsSidebarProps) {
  const progress = totalDue > 0 ? totalDone / totalDue : 0;
  const remaining = totalDue - totalDone;

  // Top 5 habits by score (non-cold-start only)
  const topHabits = useMemo(() => {
    return allHabits
      .filter((d) => !d.isColdStart)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [allHabits]);

  // Weekly trend: completion rates for each day of the week
  const weeklyBars = useMemo(() => {
    const bars: Array<{ label: string; rate: number; isToday: boolean }> = [];

    for (let i = 6; i >= 0; i--) {
      const date = addDays(today, -i);
      const dayOfWeek = new Date(date + "T12:00:00").getDay();
      const label = DAY_LABELS[dayOfWeek];

      // Count completions for this date
      const dayCompletions = completions.filter(
        (c) => c.effective_date === date && (c.type === "done" || c.type === "backfill"),
      );
      const uniqueCompleted = new Set(dayCompletions.map((c) => c.habit_id)).size;
      const total = allHabits.length;
      const rate = total > 0 ? uniqueCompleted / total : 0;

      bars.push({ label, rate, isToday: i === 0 });
    }

    return bars;
  }, [today, completions, allHabits.length]);

  // Reorder day labels based on firstDayOfWeek
  void firstDayOfWeek; // Used for future week boundary alignment

  if (allHabits.length === 0) {
    return (
      <aside className={styles.sidebar}>
        <p className={styles.emptyText}>Add habits to see your summary here.</p>
      </aside>
    );
  }

  return (
    <aside className={styles.sidebar}>
      {/* Today's progress */}
      <div>
        <h3 className={styles.sectionTitle}>Today</h3>
        <div className={styles.progressSection}>
          <ScoreRing score={progress} size={76} strokeWidth={4}>
            <span style={{ fontSize: "1rem", fontWeight: 600 }}>{Math.round(progress * 100)}%</span>
          </ScoreRing>
          <p className={styles.progressCounts}>
            <span className={styles.progressDone}>{totalDone}</span> done
            {remaining > 0 && <>, {remaining} remaining</>}
          </p>
        </div>
      </div>

      {/* Top scores */}
      {topHabits.length > 0 && (
        <div>
          <h3 className={styles.sectionTitle}>Top Scores</h3>
          <div className={styles.topScores}>
            {topHabits.map((d) => (
              <div key={d.habit.id} className={styles.scoreItem}>
                <span className={styles.scoreIcon}>
                  <HabitIcon value={d.habit.icon} />
                </span>
                <span className={styles.scoreName}>{d.habit.name}</span>
                <div className={styles.scoreBarTrack}>
                  <div
                    className={styles.scoreBarFill}
                    style={{ width: `${Math.round(d.score * 100)}%` }}
                  />
                </div>
                <span className={styles.scoreValue}>{Math.round(d.score * 100)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly trend */}
      <div>
        <h3 className={styles.sectionTitle}>This Week</h3>
        <div className={styles.weeklyTrend}>
          <div className={styles.barChart}>
            {weeklyBars.map((bar, i) => (
              <div key={i} className={styles.barWrapper}>
                <div
                  className={cn(
                    styles.bar,
                    bar.rate === 0 && styles.barEmpty,
                    bar.isToday && bar.rate > 0 && styles.barToday,
                  )}
                  style={{ height: `${Math.max(bar.rate * 100, 4)}%` }}
                />
                <span className={styles.barLabel}>{bar.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
